import { z } from "zod";

export const Photo = z.object({
  url: z.string(),
  // `.default("")`, not a bare `z.string()`: TMDB-sourced photos are stored with
  // `blurhash: ""` (`CreatePersonFromTmdbCommandHandler.BuildMedia`), AutoForm's
  // `removeEmptyValues` deletes that key before validation, and a required
  // string then fails as `media.photos.N.blurhash: Required` — an error no field
  // renders, so Submit silently did nothing on any movie, play or person
  // carrying such a photo. `""` is still what goes back: `PhotoDbDto.Blurhash`
  // is a required string.
  blurhash: z.string().default(""),
  metadata: z.any().nullish(),
});

export const Media = z.object({
  photos: z.array(Photo).min(1, "At least one photo is required"),
  teasers: z.array(
    z.object({
      url: z.string(),
      thumbnailUrl: z.string().nullish(),
      previewUrl: z.string(),
      thumbnailBlurhash: z.string().nullish(),
    }),
  ),
});

export const MediaLenient = Media.extend({
  photos: z.array(Photo).default([]),
});

export type PhotoType = z.infer<typeof Photo>;
export type TeaserType = z.infer<typeof Media>["teasers"];
export type MediaType = z.infer<typeof Media>;

/**
 * True when two photo lists differ by URL (order-sensitive). Used on edit to
 * detect that the artwork was replaced, so callers can clear `hasPlaceholderArt`
 * — a real, user-supplied image no longer counts as the bundled placeholder.
 */
export const photosDiffer = (
  a: readonly PhotoType[] | undefined,
  b: readonly PhotoType[] | undefined,
): boolean => {
  const au = (a ?? []).map((p) => p.url);
  const bu = (b ?? []).map((p) => p.url);
  return au.length !== bu.length || au.some((url, i) => url !== bu[i]);
};
